import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import type { AgentForgeBulkActivitySnapshot } from '../../../types/blocks/bulkOperation';
import {
  agentForgeBulkActivityKeys,
  listAgentForgeBulkActivities,
} from './bulkActivityApi';
import {
  AgentForgeBulkActivityContext,
  type AgentForgeBulkActivityContextValue,
} from './bulkActivityContext';

const DEFAULT_POLL_INTERVAL_MS = 5000;
const DEFAULT_RESOLUTION_GRACE_MS = 15000;

interface AgentForgeBulkActivityProviderProps {
  children: ReactNode;
  scope: string | null | undefined;
  pollIntervalMs?: number;
  resolutionGraceMs?: number;
}

const isActive = (activity: AgentForgeBulkActivitySnapshot): boolean =>
  activity.status === 'pending' || activity.status === 'in_progress';

/** AgentForge-owned exact-ID activity tracker. All mounted chat cards share one
 * recent-list query, while each card independently registers its own ID. */
export const AgentForgeBulkActivityProvider = ({
  children,
  scope,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  resolutionGraceMs = DEFAULT_RESOLUTION_GRACE_MS,
}: AgentForgeBulkActivityProviderProps) => {
  const queryClient = useQueryClient();
  const refCounts = useRef(new Map<number, number>());
  const missingSince = useRef(new Map<number, number>());
  const [watchedIds, setWatchedIds] = useState<number[]>([]);
  const [unavailableIds, setUnavailableIds] = useState(() => new Set<number>());
  const queryScope = scope ?? 'no-session';
  const queryKey = useMemo(
    () => agentForgeBulkActivityKeys.recent(queryScope),
    [queryScope]
  );

  const watchActivity = useCallback(
    (activityId: number) => {
      if (!Number.isInteger(activityId) || activityId <= 0) return () => {};

      const nextCount = (refCounts.current.get(activityId) ?? 0) + 1;
      refCounts.current.set(activityId, nextCount);

      if (nextCount === 1) {
        setWatchedIds((ids) => [...ids, activityId]);
        setUnavailableIds((ids) => {
          if (!ids.has(activityId)) return ids;
          const next = new Set(ids);
          next.delete(activityId);
          return next;
        });
        void queryClient.invalidateQueries({ queryKey, exact: true });
      }

      return () => {
        const count = refCounts.current.get(activityId) ?? 0;
        if (count > 1) {
          refCounts.current.set(activityId, count - 1);
          return;
        }

        refCounts.current.delete(activityId);
        setWatchedIds((ids) => ids.filter((id) => id !== activityId));
        setUnavailableIds((ids) => {
          if (!ids.has(activityId)) return ids;
          const next = new Set(ids);
          next.delete(activityId);
          return next;
        });
        missingSince.current.delete(activityId);
      };
    },
    [queryClient, queryKey]
  );

  const query = useQuery<AgentForgeBulkActivitySnapshot[]>({
    queryKey,
    queryFn: listAgentForgeBulkActivities,
    enabled: Boolean(scope) && watchedIds.length > 0,
    staleTime: 1000,
    retry: 3,
    retryDelay: (attempt) => Math.min(250 * 2 ** attempt, 1000),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    structuralSharing: (previous, incoming) => {
      const previousActivities =
        (previous as AgentForgeBulkActivitySnapshot[] | undefined) ?? [];
      const incomingActivities = incoming as AgentForgeBulkActivitySnapshot[];
      const incomingIds = new Set(
        incomingActivities.map((activity) => activity.id)
      );
      const retainedTerminal = previousActivities.filter(
        (activity) =>
          refCounts.current.has(activity.id) &&
          !incomingIds.has(activity.id) &&
          !isActive(activity)
      );
      return [...incomingActivities, ...retainedTerminal];
    },
    refetchInterval: (recentQuery) => {
      const activities = recentQuery.state.data ?? [];
      const shouldPoll = watchedIds.some((activityId) => {
        const activity = activities.find(
          (candidate) => candidate.id === activityId
        );
        if (!activity) return !unavailableIds.has(activityId);
        return isActive(activity);
      });
      return shouldPoll ? pollIntervalMs : false;
    },
  });

  useEffect(() => {
    if (!query.isSuccess) return;

    const activities = query.data ?? [];
    const activityIds = new Set(activities.map((activity) => activity.id));
    const now = Date.now();

    setUnavailableIds((current) => {
      let next = current;
      refCounts.current.forEach((_count, activityId) => {
        if (activityIds.has(activityId)) {
          missingSince.current.delete(activityId);
          if (next.has(activityId)) {
            next = new Set(next);
            next.delete(activityId);
          }
          return;
        }

        const firstMissAt = missingSince.current.get(activityId);
        if (firstMissAt === undefined) {
          missingSince.current.set(activityId, now);
          return;
        }
        if (now - firstMissAt >= resolutionGraceMs && !next.has(activityId)) {
          next = new Set(next).add(activityId);
        }
      });
      return next;
    });
  }, [query.data, query.dataUpdatedAt, query.isSuccess, resolutionGraceMs]);

  const activityMap = useMemo(
    () =>
      new Map((query.data ?? []).map((activity) => [activity.id, activity])),
    [query.data]
  );
  const getActivity = useCallback(
    (activityId: number) => activityMap.get(activityId),
    [activityMap]
  );
  const isActivityUnavailable = useCallback(
    (activityId: number) =>
      unavailableIds.has(activityId) && !activityMap.has(activityId),
    [activityMap, unavailableIds]
  );
  const value = useMemo<AgentForgeBulkActivityContextValue>(
    () => ({ watchActivity, getActivity, isActivityUnavailable }),
    [getActivity, isActivityUnavailable, watchActivity]
  );

  return (
    <AgentForgeBulkActivityContext.Provider value={value}>
      {children}
    </AgentForgeBulkActivityContext.Provider>
  );
};
